import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One press, one answer.
 *
 * `run(work)` takes the lock on the press itself -- a ref, not state that lands
 * a frame later -- so a second press while the first is still working does
 * nothing, however fast the second one comes. `busy` is the state to draw: the
 * control says a word (正在保存…) until `work` settles, and the end state is
 * drawn in place by whoever owns it.
 *
 * `work` is the caller's: a failure is rethrown after the lock is released, and
 * a caller that wants to show it catches it there. `holdMs` keeps the busy
 * word on screen at least that long, so an answer that comes back in a few
 * milliseconds is a word the owner can read, not a blink; the lock holds for
 * the same time.
 */
export function useBusyAction(holdMs = 0) {
  const lock = useRef(false);
  const mounted = useRef(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const run = useCallback(async <T,>(work: () => Promise<T> | T): Promise<T | undefined> => {
    if (lock.current) return undefined;
    lock.current = true;
    const started = Date.now();
    setBusy(true);
    try {
      return await work();
    } finally {
      const rest = holdMs - (Date.now() - started);
      if (rest > 0) await new Promise<void>(done => setTimeout(done, rest));
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [holdMs]);
  return { busy, run };
}
